import { t } from "../i18n";
import { micStore } from "../micStore";

const MIME_CANDIDATES: readonly string[] = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

/** Hard cap on a single recording's length; recording auto-stops here. */
const MAX_RECORD_MS = 3000;

const WAVE_W = 320;
const WAVE_H = 80;

/** Half-width (canvas px) of the grab zone around each trim boundary on the waveform, sized for touch. */
const EDGE_HIT_PX = 12;

/** Signal floor for silence trimming, relative to the clip's peak (-40 dB). */
const SILENCE_RATIO = 0.01;
/** RMS window for silence detection, so a lone spike can't anchor a bound. */
const SILENCE_RMS_WINDOW_S = 0.008;
/** Kept on each side of the detected signal so the auto-trim never clips the onset/tail. */
const SILENCE_MARGIN_S = 0.01;

const ICON_PLAY = "▶";
const ICON_STOP = "■";

/**
 * Finds the span of a clip that carries signal, so leading/trailing silence can
 * be trimmed off by default. The threshold is peak-relative (SILENCE_RATIO)
 * measured over short non-overlapping RMS windows. Returns null when the clip is
 * fully silent or the signal already spans the whole buffer (the caller then
 * keeps the full length).
 */
export function detectSilenceBounds(data: Float32Array, sampleRate: number): { start: number; end: number } | null {
  const n = data.length;
  if (n === 0 || sampleRate <= 0) {
    return null;
  }
  let peak = 0;
  for (let i = 0; i < n; i++) {
    const a = Math.abs(data[i]);
    if (a > peak) {
      peak = a;
    }
  }
  if (peak === 0) {
    return null;
  }
  const threshold = peak * SILENCE_RATIO;
  const win = Math.max(1, Math.round(sampleRate * SILENCE_RMS_WINDOW_S));
  let firstSample = -1;
  let lastSample = -1;
  for (let s = 0; s < n; s += win) {
    const end = Math.min(n, s + win);
    let sum = 0;
    for (let i = s; i < end; i++) {
      sum += data[i] * data[i];
    }
    if (Math.sqrt(sum / (end - s)) > threshold) {
      if (firstSample < 0) {
        firstSample = s;
      }
      lastSample = end;
    }
  }
  if (firstSample < 0) {
    return null;
  }
  const dur = n / sampleRate;
  const start = Math.max(0, firstSample / sampleRate - SILENCE_MARGIN_S);
  const end = Math.min(dur, lastSample / sampleRate + SILENCE_MARGIN_S);
  if (start <= 0 && end >= dur) {
    return null;
  }
  return { start, end };
}

/** The first MediaRecorder mime this browser supports, or "" to let it pick its own default. */
function pickMime(): string {
  for (const m of MIME_CANDIDATES) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return "";
}

/**
 * Opens the mic recording modal: a state machine over getUserMedia +
 * MediaRecorder — idle → recording → recorded (waveform / trim range / region
 * preview / retake / name / save). The recording and recorded states render the
 * same set of controls (the waveform with draggable trim boundaries, region
 * button, name and notice are present, only enabled once a clip exists) so the action buttons
 * keep their position when recording stops. The recorded blob is decoded into an
 * AudioBuffer for the waveform and dragging the region's left/right boundary on
 * the waveform picks the start/end to keep; saving stores those trim bounds alongside the original
 * blob (no re-encode), so the `mic` timbre plays only the selected region while
 * older recordings without trim metadata still play full-length. Saving appends
 * the blob to the shared mic store, which persists it to this device's
 * IndexedDB only. A getUserMedia failure (denied permission, unsupported
 * browser, insecure context) surfaces an inline error and never throws; closing
 * always stops the stream, stops any region preview, and closes the decode
 * context. `onSimPause` is toggled true while the modal is open so the caller
 * can freeze the scene simulation and mute its audio (which would otherwise
 * bleed into the recording), and false again when it closes.
 */
export function showRecordModal(onSimPause: (open: boolean) => void): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const box = document.createElement("div");
  box.className = "modal-box record-modal";

  const titleEl = document.createElement("div");
  titleEl.className = "modal-title";
  titleEl.textContent = t("record.title");

  const statusEl = document.createElement("div");
  statusEl.className = "modal-message record-status";
  statusEl.style.display = "none";

  const waveCanvas = document.createElement("canvas");
  waveCanvas.className = "record-wave";
  waveCanvas.width = WAVE_W;
  waveCanvas.height = WAVE_H;

  const waveArea = document.createElement("div");
  waveArea.className = "record-wave-area";
  waveArea.style.display = "none";
  const waveOverlay = document.createElement("div");
  waveOverlay.className = "record-wave-overlay";
  const overlayText = document.createElement("div");
  overlayText.className = "record-wave-overlay-text";
  waveOverlay.append(overlayText);
  waveArea.append(waveCanvas, waveOverlay);

  const regionBtn = document.createElement("button");
  regionBtn.className = "mic-btn record-region-btn";
  regionBtn.textContent = ICON_PLAY;
  regionBtn.style.display = "none";

  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.className = "num-input";
  nameInput.placeholder = t("record.namePlaceholder");
  nameInput.style.display = "none";

  const notice = document.createElement("div");
  notice.className = "modal-message record-local-only";
  notice.textContent = t("record.localOnly");
  notice.style.display = "none";

  const actions = document.createElement("div");
  actions.className = "modal-actions";

  box.append(titleEl, statusEl, waveArea, regionBtn, nameInput, notice, actions);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  onSimPause(true);

  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let blob: Blob | null = null;
  let autoStop: number | null = null;
  let progressRaf: number | null = null;
  let recordStartedAt = 0;

  let audioCtx: AudioContext | null = null;
  let audioBuffer: AudioBuffer | null = null;
  let trimStart = 0;
  let trimEnd = 0;
  let trimEnabled = false;
  let regionSource: AudioBufferSourceNode | null = null;
  let regionRaf: number | null = null;
  let regionStartedAt = 0;

  function clearAutoStop(): void {
    if (autoStop !== null) {
      clearTimeout(autoStop);
      autoStop = null;
    }
  }

  /** Fills the whole wave-area overlay left-to-right from 0 to 1 across MAX_RECORD_MS so the area itself reads as the recording progress indicator. */
  function tickProgress(): void {
    const p = Math.min(1, (performance.now() - recordStartedAt) / MAX_RECORD_MS);
    const pct = p * 100;
    waveOverlay.style.background = `linear-gradient(to right, var(--accent-bg) ${pct}%, var(--surface) ${pct}%)`;
    progressRaf = p < 1 ? requestAnimationFrame(tickProgress) : null;
  }

  function stopProgressRaf(): void {
    if (progressRaf !== null) {
      cancelAnimationFrame(progressRaf);
      progressRaf = null;
    }
  }

  function stopStream(): void {
    if (stream !== null) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      stream = null;
    }
  }

  /** Smallest selectable region, so the two thumbs can never coincide (which would make one ungrabbable) or pick a zero-length region. */
  function minGap(): number {
    const dur = audioBuffer?.duration ?? 0;
    return Math.min(dur, Math.max(0.02, dur * 0.01));
  }

  function drawWave(playhead: number | null): void {
    const g = waveCanvas.getContext("2d");
    if (g === null) {
      return;
    }
    g.clearRect(0, 0, WAVE_W, WAVE_H);
    if (audioBuffer === null) {
      return;
    }
    const data = audioBuffer.getChannelData(0);
    const dur = audioBuffer.duration;
    const mid = WAVE_H / 2;
    const step = Math.max(1, Math.floor(data.length / WAVE_W));
    g.strokeStyle = "rgba(255, 255, 255, 0.5)";
    g.beginPath();
    for (let x = 0; x < WAVE_W; x++) {
      let min = 1;
      let max = -1;
      const s = x * step;
      for (let i = 0; i < step && s + i < data.length; i++) {
        const v = data[s + i];
        if (v < min) {
          min = v;
        }
        if (v > max) {
          max = v;
        }
      }
      g.moveTo(x + 0.5, mid + min * mid);
      g.lineTo(x + 0.5, mid + max * mid);
    }
    g.stroke();
    const x0 = (trimStart / dur) * WAVE_W;
    const x1 = (trimEnd / dur) * WAVE_W;
    g.fillStyle = "rgba(91, 157, 255, 0.25)";
    g.fillRect(x0, 0, x1 - x0, WAVE_H);
    g.fillStyle = "rgba(91, 157, 255, 0.9)";
    g.fillRect(x0 - 1, 0, 2, WAVE_H);
    g.fillRect(x1 - 1, 0, 2, WAVE_H);
    if (playhead !== null) {
      g.fillStyle = "rgba(255, 255, 255, 0.9)";
      g.fillRect((playhead / dur) * WAVE_W - 0.5, 0, 1, WAVE_H);
    }
  }

  function stopRegion(): void {
    if (regionSource !== null) {
      regionSource.onended = null;
      regionSource.stop();
      regionSource.disconnect();
      regionSource = null;
    }
    if (regionRaf !== null) {
      cancelAnimationFrame(regionRaf);
      regionRaf = null;
    }
    if (audioBuffer !== null) {
      trimEnabled = true;
    }
    regionBtn.textContent = ICON_PLAY;
    drawWave(null);
  }

  function playRegion(): void {
    if (audioBuffer === null) {
      return;
    }
    if (regionSource !== null) {
      stopRegion();
      return;
    }
    const playS = Math.max(0, trimEnd - trimStart);
    if (playS <= 0) {
      return;
    }
    if (audioCtx === null) {
      audioCtx = new AudioContext();
    }
    const ctx = audioCtx;
    void ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    regionStartedAt = ctx.currentTime;
    source.start(0, trimStart, playS);
    regionSource = source;
    trimEnabled = false;
    regionBtn.textContent = ICON_STOP;
    source.onended = (): void => stopRegion();
    const tick = (): void => {
      if (regionSource === null) {
        return;
      }
      drawWave(trimStart + (ctx.currentTime - regionStartedAt));
      regionRaf = requestAnimationFrame(tick);
    };
    regionRaf = requestAnimationFrame(tick);
  }

  /** Shows the wave area / trim controls with the recording overlay hidden; `enabled` is false until a clip exists (the controls hold their place but are inert). */
  function showTrimUi(enabled: boolean): void {
    waveArea.style.display = "";
    waveOverlay.classList.remove("show");
    regionBtn.style.display = "";
    regionBtn.textContent = ICON_PLAY;
    trimEnabled = enabled;
    regionBtn.disabled = !enabled;
  }

  function hideTrimUi(): void {
    stopRegion();
    waveArea.style.display = "none";
    waveOverlay.classList.remove("show");
    regionBtn.style.display = "none";
  }

  /** Decodes the recorded blob into an AudioBuffer, resets the trim region to the full clip, and draws the waveform. */
  async function loadWaveform(): Promise<void> {
    audioBuffer = null;
    if (blob === null) {
      return;
    }
    if (audioCtx === null) {
      audioCtx = new AudioContext();
    }
    try {
      const buf = await audioCtx.decodeAudioData(await blob.arrayBuffer());
      audioBuffer = buf;
      const bounds = detectSilenceBounds(buf.getChannelData(0), buf.sampleRate);
      trimStart = bounds !== null ? bounds.start : 0;
      trimEnd = bounds !== null ? bounds.end : buf.duration;
      trimEnabled = true;
      regionBtn.disabled = false;
      drawWave(null);
    } catch (e: unknown) {
      console.error("waveform decode failed", e);
    }
  }

  function setTrimStart(v: number): void {
    const gap = minGap();
    if (v > trimEnd - gap) {
      v = trimEnd - gap;
    }
    if (v < 0) {
      v = 0;
    }
    trimStart = v;
    drawWave(null);
  }

  function setTrimEnd(v: number): void {
    const dur = audioBuffer?.duration ?? 0;
    const gap = minGap();
    if (v < trimStart + gap) {
      v = trimStart + gap;
    }
    if (v > dur) {
      v = dur;
    }
    trimEnd = v;
    drawWave(null);
  }

  let dragEdge: "start" | "end" | null = null;

  /** Maps a pointer event to a waveform-canvas x in WAVE_W coordinates, correcting for any CSS scaling of the canvas. */
  function canvasX(ev: PointerEvent): number {
    const rect = waveCanvas.getBoundingClientRect();
    return ((ev.clientX - rect.left) / rect.width) * WAVE_W;
  }

  /** Returns which trim boundary lies within EDGE_HIT_PX of x (the nearer one when both are in range), or null. */
  function edgeAt(x: number): "start" | "end" | null {
    if (audioBuffer === null) {
      return null;
    }
    const dur = audioBuffer.duration;
    const d0 = Math.abs(x - (trimStart / dur) * WAVE_W);
    const d1 = Math.abs(x - (trimEnd / dur) * WAVE_W);
    if (d0 <= EDGE_HIT_PX && d0 <= d1) {
      return "start";
    }
    if (d1 <= EDGE_HIT_PX) {
      return "end";
    }
    return null;
  }

  waveCanvas.addEventListener("pointerdown", (ev: PointerEvent): void => {
    if (audioBuffer === null || !trimEnabled) {
      return;
    }
    const edge = edgeAt(canvasX(ev));
    if (edge === null) {
      return;
    }
    ev.preventDefault();
    dragEdge = edge;
    waveCanvas.setPointerCapture(ev.pointerId);
  });

  waveCanvas.addEventListener("pointermove", (ev: PointerEvent): void => {
    if (audioBuffer === null) {
      return;
    }
    const x = canvasX(ev);
    if (dragEdge === null) {
      waveCanvas.style.cursor = trimEnabled && edgeAt(x) !== null ? "ew-resize" : "";
      return;
    }
    const dur = audioBuffer.duration;
    const sec = Math.max(0, Math.min(dur, (x / WAVE_W) * dur));
    if (dragEdge === "start") {
      setTrimStart(sec);
    } else {
      setTrimEnd(sec);
    }
  });

  function endCanvasDrag(ev: PointerEvent): void {
    if (dragEdge === null) {
      return;
    }
    dragEdge = null;
    if (waveCanvas.hasPointerCapture(ev.pointerId)) {
      waveCanvas.releasePointerCapture(ev.pointerId);
    }
  }
  waveCanvas.addEventListener("pointerup", endCanvasDrag);
  waveCanvas.addEventListener("pointercancel", endCanvasDrag);

  regionBtn.addEventListener("click", playRegion);

  function close(): void {
    clearAutoStop();
    stopProgressRaf();
    stopRegion();
    if (recorder !== null && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    stopStream();
    if (audioCtx !== null) {
      void audioCtx.close();
      audioCtx = null;
    }
    overlay.remove();
    onSimPause(false);
  }

  function button(label: string, onClick: () => void, rightStart: boolean = false, subtle: boolean = false): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.textContent = label;
    if (rightStart) {
      btn.classList.add("record-action-right");
    }
    if (subtle) {
      btn.classList.add("modal-btn-subtle");
    }
    btn.addEventListener("click", onClick);
    return btn;
  }

  function clearActions(): void {
    actions.textContent = "";
  }

  /** Sets the status line, collapsing it when empty so it adds no gap between the title and the wave area (it only ever carries error text). */
  function setStatus(message: string): void {
    statusEl.textContent = message;
    statusEl.style.display = message !== "" ? "" : "none";
  }

  function renderIdle(): void {
    setStatus("");
    stopProgressRaf();
    hideTrimUi();
    nameInput.style.display = "none";
    notice.style.display = "none";
    clearActions();
    actions.append(button(t("record.start"), (): void => void start()), button(t("record.cancel"), close, true, true));
  }

  function renderRecording(): void {
    setStatus("");
    audioBuffer = null;
    showTrimUi(false);
    drawWave(null);
    overlayText.textContent = t("record.recording");
    waveOverlay.style.background = "var(--surface)";
    waveOverlay.classList.add("show");
    nameInput.style.display = "";
    nameInput.disabled = true;
    notice.style.display = "";
    recordStartedAt = performance.now();
    stopProgressRaf();
    progressRaf = requestAnimationFrame(tickProgress);
    clearActions();
    actions.append(button(t("record.stop"), stop), button(t("record.cancel"), close, true, true));
  }

  function renderRecorded(): void {
    setStatus("");
    stopProgressRaf();
    showTrimUi(false);
    nameInput.style.display = "";
    nameInput.disabled = false;
    notice.style.display = "";
    nameInput.focus();
    clearActions();
    actions.append(
      button(t("record.retake"), (): void => void start()),
      button(t("record.save"), save, true),
      button(t("record.cancel"), close, false, true),
    );
    void loadWaveform();
  }

  function showError(message: string): void {
    setStatus(message);
    stopProgressRaf();
    hideTrimUi();
    nameInput.style.display = "none";
    notice.style.display = "none";
    clearActions();
    actions.append(button(t("record.cancel"), close, true));
  }

  async function start(): Promise<void> {
    if (typeof navigator === "undefined" || navigator.mediaDevices?.getUserMedia === undefined || typeof MediaRecorder === "undefined") {
      showError(t("record.unsupported"));
      return;
    }
    stopStream();
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e: unknown) {
      console.error("getUserMedia failed", e);
      showError(t("record.permissionDenied"));
      return;
    }
    const mime = pickMime();
    chunks = [];
    blob = null;
    recorder = mime !== "" ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    recorder.ondataavailable = (ev: BlobEvent): void => {
      if (ev.data.size > 0) {
        chunks.push(ev.data);
      }
    };
    recorder.onstop = (): void => {
      clearAutoStop();
      blob = new Blob(chunks, { type: recorder?.mimeType !== undefined && recorder.mimeType !== "" ? recorder.mimeType : "audio/webm" });
      stopStream();
      renderRecorded();
    };
    recorder.start();
    autoStop = window.setTimeout(stop, MAX_RECORD_MS);
    renderRecording();
  }

  function stop(): void {
    clearAutoStop();
    if (recorder !== null && recorder.state !== "inactive") {
      recorder.stop();
    }
  }

  function save(): void {
    if (blob === null) {
      return;
    }
    const name = nameInput.value.trim() !== "" ? nameInput.value.trim() : `${t("record.defaultName")} ${micStore.count() + 1}`;
    if (audioBuffer !== null) {
      void micStore.add(blob, name, trimStart, trimEnd);
    } else {
      void micStore.add(blob, name);
    }
    close();
  }

  renderIdle();
}
